import { isEmpty, isEmail, isSSN, isTFN, formatMobilePhone, isValidIsraelID } from '@jjj/components';
import { isMobilePhone } from '../../../helpers/validators';
import { COUNTRIES } from '../../../helpers/countries';
import actions from './actions';
import { createReducer, getError } from '../../../helpers/reducer';
import { keyBy } from '../../../helpers/core';
import { isBench } from '../../../settings';
import { setDefaultCountryField, isAUS, isISR, isUSA, isGBR, isPostalCode } from '../../../helpers/countriesHelpers';
import { isSSNorTFNMandatory } from '../utils';
import { t } from '../../../i18n';

const initState = {
  applicant: null,
  editForm: null,
  isEdit: false,
  loading: false,
  applicantRequestIsPending: false,
  applicantPendingAction: '',
  deleted: false,
  sickAndLeaveRadio: isBench() ? 'Yes' : 'No',
  states: [],
  formInfo: {
    countries: [],
  },
  isChangeStatusPending: false,
};

const initialApplicantData = {
  address: '',
  birthday: '',
  city: '',
  cost_center: '',
  country: '',
  e_verify_case_id: '',
  email: '',
  employee_id: '',
  first_name: '',
  gender: '',
  i9_signed: '',
  intercom_denial_notice_sent: false,
  last_name: '',
  legal_first_name: '',
  legal_last_name: '',
  legal_middle_name: '',
  phone_number: '',
  pto_hours_accrued: 0,
  sick_hours: [{ state_id: '', hours: 0 }],
  skills: [],
  ssn: '',
  israeli_id: '',
  tax_information: {
    id: null,
    tax_file_number: '',
  },
  state: '',
  status: '',
  street2: '',
  street: '',
  zip: '',
};

function onLoad(state) {
  return {
    ...state,
    loading: true,
    isEdit: false,
  };
}

function onLoadSuccess(state, { payload }) {
  const { data } = payload;
  const applicant = {
    ...data,
    address: '',
  };

  return {
    ...state,
    applicant,
    sickAndLeaveRadio: (data.sick_hours && data.sick_hours.length) || isBench() ? 'Yes' : 'No',
    isChangeStatusPending: false,
    loading: false,
  };
}

function changeStatus(state) {
  return { ...state, isChangeStatusPending: true };
}

function onClearStore() {
  return {
    ...initState,
  };
}

function onSendApplicantRequest(state, { payload }) {
  return { ...state, applicantRequestIsPending: true, applicantPendingAction: payload.applicantPendingAction };
}

function onSendApplicantRequestSuccess(state) {
  return { ...state, applicantRequestIsPending: false, applicantPendingAction: '' };
}

function onSendApplicantRequestError(state) {
  return { ...state, applicantRequestIsPending: false, applicantPendingAction: '' };
}

function validate(attr, value, state, params) {
  let error = '';
  const { editForm, formInfo } = state;

  if (isUSA() && attr === 'ssn' && !formInfo?.hidden_fields?.includes('ssn')) {
    if (isEmpty(value, true)) {
      if (
        editForm?.status &&
        formInfo?.statuses &&
        isSSNorTFNMandatory(
          formInfo.statuses.map(item => item.id),
          editForm.status.value,
        )
      ) {
        error = t('dash.validation.required_error', { fieldName: t('dash.applicants.social_security_number_label') });
      }
    } else if (!isSSN(value)) {
      error = t('dash.validation.valid_error', { fieldName: t('dash.applicants.social_security_number_label') });
    }
  }

  if (isISR() && attr === 'israeli_id' && !formInfo?.hidden_fields?.includes('israeli_id')) {
    if (!isValidIsraelID(value)) {
      error = t('dash.validation.valid_error', { fieldName: t('dash.applicants.israel_national_id_label') });
    }
  }

  if (isAUS() && attr === 'tax_information' && !formInfo?.hidden_fields?.includes('tax_information')) {
    if (isEmpty(value, true)) {
      if (
        editForm?.status &&
        formInfo?.statuses &&
        isSSNorTFNMandatory(
          formInfo.statuses.map(item => item.id),
          editForm.status.value,
        )
      ) {
        error = t('dash.validation.required_error', { fieldName: t('dash.applicants.tax_file_number_label') });
      }
    } else if (!isTFN(value)) {
      error = t('dash.validation.valid_error', { fieldName: t('dash.applicants.tax_file_number_label') });
    }
  }

  if (attr === 'first_name') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.applicants.first_name_label') });
    }
  }

  if (attr === 'last_name') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.applicants.last_name_label') });
    }
  }

  if (attr === 'email') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.applicants.email_label') });
    } else if (!isEmail(value)) {
      error = t('dash.validation.valid_error', { fieldName: t('dash.applicants.email_label') });
    }
  }

  if (attr === 'phone_number') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.applicants.phone_number_label') });
    } else if (!isMobilePhone(formatMobilePhone(value), params?.phoneNumberCountry)) {
      error = t('dash.validation.valid_error', { fieldName: t('dash.applicants.phone_number_label') });
    }
  }

  if (attr === 'country') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.applicants.country_label') });
    }
  }

  if (attr === 'zip') {
    const countries = formInfo?.countries || [];
    const countryId = editForm?.country?.value;
    const selectedCountryName = countries.find(country => country.id === countryId)?.name;
    const selectedCountryAlpha2Code = COUNTRIES[selectedCountryName]?.alpha_2_code;

    if (selectedCountryAlpha2Code && !isPostalCode(value, selectedCountryAlpha2Code)) {
      error = t('dash.validation.valid_error', {
        fieldName: t('dash.common.location.postal_code'),
      });
    }
  }

  return error;
}

function onEdit(state, { payload }) {
  const skills = payload.applicant.skills.map(item => {
    return {
      ...item,
      _destroy: false,
    };
  });

  const applicant = { ...payload.applicant, skills };
  const propsArray = Object.keys(applicant);

  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value:
        applicant[prop] && prop !== 'tax_information' && Object.prototype.hasOwnProperty.call(applicant[prop], 'id')
          ? applicant[prop].id
          : applicant[prop],
      error: validate(
        prop,
        prop === 'tax_information' ? applicant[prop] && applicant[prop].tax_file_number : applicant[prop],
        state,
      ),
      touched: false,
    }),
  );

  return {
    ...state,
    isEdit: true,
    editForm,
  };
}

function onSaveSuccess(state) {
  return {
    ...state,
    isEdit: false,
    editForm: null,
  };
}

function onSaveError(state, { payload }) {
  const { data } = payload;

  const propsArray = Object.entries(state.editForm);
  const editForm = keyBy(
    propsArray,
    entry => entry[0],
    entry => {
      const [fieldName, value] = entry;

      if (fieldName === 'skills' && data?.competencies) {
        const skillsIndexes = Object.keys(data.competencies);

        skillsIndexes.forEach(skillIndex => {
          value.value[skillIndex].error = data.competencies[skillIndex].position_id;
        });
      }

      return {
        ...value,
        error: getError(fieldName, data),
        touched: true,
      };
    },
  );

  return {
    ...state,
    editForm,
  };
}

function onDeleteSuccess(state) {
  return {
    ...state,
    isEdit: false,
    editForm: null,
    deleted: true,
  };
}

function onCreate(state) {
  const propsArray = Object.keys(initialApplicantData);
  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: initialApplicantData[prop],
      error: validate(prop, initialApplicantData[prop], state),
      touched: false,
    }),
  );

  return {
    ...state,
    isEdit: true,
    applicant: initialApplicantData,
    editForm: {
      ...editForm,
      tax_information: {
        value: initialApplicantData.tax_information,
        error: '',
        name: 'tax_information',
        touched: true,
      },
      country: setDefaultCountryField(),
    },
  };
}

function onDeleteSkill(state, { payload }) {
  const { skillId } = payload;
  const skillsArray = state.editForm.skills.value;
  const index = skillsArray.findIndex(item => {
    return item.id === skillId;
  });

  if (skillsArray?.[index]?.newSkill) {
    skillsArray.splice(index, 1);
  } else {
    skillsArray[index] = {
      ...skillsArray[index],
      _destroy: true,
    };
  }

  return {
    ...state,
    editForm: {
      ...state.editForm,
      skills: {
        ...state.editForm.skills,
        value: skillsArray,
      },
    },
  };
}

function onAddSkillOnAddPage(state) {
  const { editForm } = state;
  const { value } = editForm.skills;

  value.push({
    id: new Date().getTime().toString(),
    position_name: '',
    position_id: '',
  });

  editForm.skills = {
    ...state.editForm.skills,
    value,
  };

  return {
    ...state,
    editForm,
  };
}

function onAddSkillOnEditPage(state) {
  const { editForm } = state;
  const { candidate_or_above, skills } = editForm;
  const { value } = skills;

  if (!candidate_or_above.value) {
    value.push({
      id: new Date().getTime().toString(),
      position_name: '',
      position_id: '',
      _destroy: false,
      newSkill: true,
    });
  } else {
    value.push({
      id: new Date().getTime().toString(),
      position_name: '',
      position_id: '',
      level: '',
      _destroy: false,
      newSkill: true,
    });
  }

  editForm.skills = {
    ...state.editForm.skills,
    value,
  };

  return {
    ...state,
    editForm,
  };
}

function formatValue(attr, value) {
  if (attr === 'zip' && !isGBR()) {
    return value.replace(/[^0-9]/g, '');
  }

  return value;
}

function onChangeProperty(state, { payload }) {
  const { attr, index, params } = payload;
  const value = formatValue(attr, payload.value);
  const { editForm } = state;

  if (index !== undefined) {
    editForm[attr].value[index] = value;
  } else {
    editForm[attr] = {
      value,
      error: validate(attr, value, state, params),
      name: attr,
      touched: true,
    };
  }

  return {
    ...state,
    editForm: {
      ...editForm,
    },
  };
}

function onChangeSkill(state, { payload }) {
  const { skillId, selectedValue } = payload;
  const { editForm, formInfo } = state;
  const { value } = editForm.skills;

  const enumerableValue = formInfo.positions_levels.find(item => item.id === selectedValue) || '';

  if (enumerableValue) {
    value.forEach(item => {
      if (item.id === skillId) {
        const [firstLevel] = enumerableValue.levels;

        item.position_name = enumerableValue.name;
        item.position_id = selectedValue;
        item.level = firstLevel;
        item.error = null;
      }

      return item;
    });
  }

  editForm.skills = {
    value,
    error: '',
    name: 'skills',
    touched: true,
  };

  return {
    ...state,
    editForm: {
      ...editForm,
    },
  };
}

function onChangeTaxInformation(state, { payload }) {
  const { editForm } = state;
  const { value } = payload;

  editForm.tax_information = {
    value: { ...editForm.tax_information.value, tax_file_number: value },
    error: validate('tax_information', value, state),
    name: 'tax_information',
    touched: true,
  };

  return {
    ...state,
    editForm: {
      ...editForm,
    },
  };
}

function onChangeLevel(state, { payload }) {
  const { levelId, selectedValue } = payload;
  const { editForm } = state;
  const { value } = editForm.skills;

  value.forEach(item => {
    if (item.id === levelId) {
      item.level = selectedValue;
    }

    return item;
  });

  editForm.skills = {
    value,
    error: '',
    name: 'skills',
    touched: true,
  };

  return {
    ...state,
    editForm: {
      ...editForm,
    },
  };
}

function onChangeProperties(state, { payload }) {
  const { attributes } = payload;
  const propsArray = Object.keys(attributes);

  const editFormProps = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: attributes[prop],
      error: validate(prop, attributes[prop], state),
      touched: true,
    }),
  );

  const editForm = {
    ...state.editForm,
    ...editFormProps,
  };

  return {
    ...state,
    editForm,
  };
}

function onCancel(state) {
  return {
    ...state,
    isEdit: false,
    editForm: null,
  };
}

function onTouch(state, { payload }) {
  const propsArray = Object.entries(payload.editForm);
  const editForm = keyBy(
    propsArray,
    entry => entry[0],
    entry => {
      const [, value] = entry;

      return {
        ...value,
        touched: true,
      };
    },
  );

  return {
    ...state,
    editForm,
  };
}

function onLoadFormData(state, { payload }) {
  const { data, states = [] } = payload;

  return {
    ...state,
    formInfo: data,
    states,
  };
}

function onChangeSickLeaveRadioBtn(state, { payload }) {
  return {
    ...state,
    sickAndLeaveRadio: payload,
  };
}

export { validate };
export default createReducer(initState, {
  [actions.ADD_SKILL_ADD_PAGE]: onAddSkillOnAddPage,
  [actions.ADD_SKILL_EDIT_PAGE]: onAddSkillOnEditPage,
  [actions.CANCEL]: onCancel,
  [actions.CHANGE_LEVEL]: onChangeLevel,
  [actions.CHANGE_PROPERTIES]: onChangeProperties,
  [actions.CHANGE_PROPERTY]: onChangeProperty,
  [actions.CHANGE_SICK_LEAVE_RADIO]: onChangeSickLeaveRadioBtn,
  [actions.CHANGE_SKILL]: onChangeSkill,
  [actions.CHANGE_STATUS]: changeStatus,
  [actions.CHANGE_TAX_INFORMATION]: onChangeTaxInformation,
  [actions.CLEAR_STORE]: onClearStore,
  [actions.CREATE]: onCreate,
  [actions.DELETE_SKILL]: onDeleteSkill,
  [actions.DELETE_SUCCESS]: onDeleteSuccess,
  [actions.EDIT]: onEdit,
  [actions.LOAD]: onLoad,
  [actions.SEND_REQUEST]: onSendApplicantRequest,
  [actions.SEND_REQUEST_SUCCESS]: onSendApplicantRequestSuccess,
  [actions.SEND_REQUEST_ERROR]: onSendApplicantRequestError,
  [actions.LOAD_FORM_DATA]: onLoadFormData,
  [actions.LOAD_SUCCESS]: onLoadSuccess,
  [actions.SAVE_ERROR]: onSaveError,
  [actions.SAVE_SUCCESS]: onSaveSuccess,
  [actions.TOUCH]: onTouch,
});
